/**
 * URL and ID validation utilities for security.
 */

/**
 * Options for `isSafeUrl`.
 */
export interface IsSafeUrlOptions {
  /**
   * If true, only `https://` URLs are accepted. `http://` URLs are rejected.
   * Defaults to false (both `http://` and `https://` accepted) for backward
   * compatibility with legacy data.
   */
  requireHttps?: boolean;
  /**
   * If true, emit a `console.warn` when the URL is rejected. Useful for
   * surfacing suspicious URLs in development/staging logs. Defaults to false.
   * Avoid enabling this in hot render paths (e.g. table cells) to prevent
   * log spam.
   */
  warn?: boolean;
}

/**
 * Check if a URL is safe to display/navigate to.
 *
 * Defense-in-depth approach: this is a frontend validation layer that
 * complements backend URL validation. Even if the backend validates URLs, we
 * re-validate on the frontend before rendering to protect against:
 * - XSS via `javascript:` URLs
 * - Data exfiltration via `data:` URLs
 * - Compromised/malicious backend responses
 *
 * By default only `http://` and `https://` protocols are allowed. Pass
 * `{ requireHttps: true }` to restrict to HTTPS only — recommended for
 * URLs coming from potentially untrusted sources (e.g. operator/grant
 * sites) to mitigate MITM attacks.
 *
 * @param url - The URL string to validate (nullable for ergonomics)
 * @param options - Optional flags: `requireHttps`, `warn`
 * @returns true if URL passes the protocol policy, false otherwise
 */
export const isSafeUrl = (
  url: string | null | undefined,
  options?: IsSafeUrlOptions,
): boolean => {
  if (!url || typeof url !== "string") return false;
  const lower = url.toLowerCase().trim();

  const hasHttps = lower.startsWith("https://");
  const hasHttp = lower.startsWith("http://");

  if (!hasHttps && !hasHttp) {
    if (options?.warn) {
      console.warn(
        `[SEC-007] Unsafe URL protocol rejected: ${url.substring(0, 50)}...`,
      );
    }
    return false;
  }

  if (options?.requireHttps && hasHttp) {
    if (options?.warn) {
      console.warn(
        `[SEC-007] Non-HTTPS URL detected: ${url.substring(0, 50)}...`,
      );
    }
    return false;
  }

  return true;
};

/**
 * UUID v4 regex pattern for validation.
 * Validates standard UUID format: xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx
 */
const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Check if a string is a valid UUID format.
 * Validates before making API calls to prevent unnecessary server load.
 * @param id - The string to validate as UUID
 * @returns true if valid UUID format, false otherwise
 */
export const isValidUUID = (id: string | undefined | null): boolean => {
  if (!id) return false;
  return UUID_REGEX.test(id);
};
